<template>
  <section class="bg-functionalities" id="features">
    <div class="content">
      <div class="functionalities-header">
        <h1 class="title">{{ $t('sections.features.title') }}</h1>
        <p>
          {{ $t('sections.features.subtitle') }}
        </p>
      </div>

      <ul class="functionalities-list">
        <li
          v-for="(feature, index) in features"
          :key="index"
          class="item"
          :class="{
            [feature.borderClass]: true,
            'is-selected': selectedIndex === index,
          }"
          @click="toggleFeatureDetails(index)"
        >
          <img class="icon-function" :src="feature.icon" :alt="feature.title" />
          <div>
            <h2
              class="font-grd"
              :class="feature.textClass"
              v-html="feature.title"
            />
            <p>{{ feature.shortDescription }}</p>
          </div>
        </li>
      </ul>

      <div v-if="selectedIndex !== null" class="details-section">
        <Splide :options="splideOptions" ref="splide" class="mt-4">
          <SplideSlide v-for="(feature, index) in features" :key="index">
            <div class="slide-content">
              <div class="lottie-container">
                <Vue3Lottie :animationData="feature.lottie" />
              </div>

              <div class="slide-text">
                <h2 class="title" v-html="feature.title" />
                <p>{{ feature.longDescription }}</p>
              </div>
            </div>
          </SplideSlide>
        </Splide>
      </div>
    </div>
  </section>
</template>

<script>
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import '@splidejs/splide/dist/css/themes/splide-default.min.css'
import NfseAnimation from '@/assets/lottie/nfse.json'
import ReportsAnimation from '@/assets/lottie/reports.json'
import TransactionsAnimation from '@/assets/lottie/transactions.json'
import AccountsAnimation from '@/assets/lottie/accounts.json'
import MulticurrencyAnimation from '@/assets/lottie/multicurrency.json'
import ContractsAnimation from '@/assets/lottie/contracts.json'
import EmailsAnimation from '@/assets/lottie/emails.json'
import StatementsAnimation from '@/assets/lottie/statements.json'

export default {
  name: 'FeaturesComponent',
  components: {
    Splide,
    SplideSlide,
  },
  data() {
    return {
      selectedIndex: 0,
    }
  },
  computed: {
    features() {
      return [
        {
          title: this.$t('sections.features.items.nfse.title'),
          icon: require('@/assets/icons/icone-emissão-min.png'),
          lottie: NfseAnimation,
          shortDescription: this.$t(
            'sections.features.items.nfse.shortDescription'
          ),
          longDescription: this.$t(
            'sections.features.items.nfse.longDescription'
          ),
          borderClass: 'border-gradient-green',
          textClass: 'bg-gdr-fonts-green',
        },
        {
          title: this.$t('sections.features.items.reports.title'),
          icon: require('@/assets/icons/icone-relatorio-min.png'),
          lottie: ReportsAnimation,
          shortDescription: this.$t(
            'sections.features.items.reports.shortDescription'
          ),
          longDescription: this.$t(
            'sections.features.items.reports.longDescription'
          ),
          borderClass: 'border-gradient-blue',
          textClass: 'bg-gdr-fonts-blue',
        },
        {
          title: this.$t('sections.features.items.transactions.title'),
          icon: require('@/assets/icons/icone-movimentacao-min.png'),
          lottie: TransactionsAnimation,
          shortDescription: this.$t(
            'sections.features.items.transactions.shortDescription'
          ),
          longDescription: this.$t(
            'sections.features.items.transactions.longDescription'
          ),
          borderClass: 'border-gradient-yellow',
          textClass: 'bg-gdr-fonts-yellow',
        },
        {
          title: this.$t('sections.features.items.accounts.title'),
          icon: require('@/assets/icons/icone-conta-min.png'),
          lottie: AccountsAnimation,
          shortDescription: this.$t(
            'sections.features.items.accounts.shortDescription'
          ),
          longDescription: this.$t(
            'sections.features.items.accounts.longDescription'
          ),
          borderClass: 'border-gradient-orange',
          textClass: 'bg-gdr-fonts-orange',
        },
        {
          title: this.$t('sections.features.items.multicurrency.title'),
          icon: require('@/assets/icons/icone-dinheiro-min.png'),
          lottie: MulticurrencyAnimation,
          shortDescription: this.$t(
            'sections.features.items.multicurrency.shortDescription'
          ),
          longDescription: this.$t(
            'sections.features.items.multicurrency.longDescription'
          ),
          borderClass: 'border-gradient-red',
          textClass: 'bg-gdr-fonts-red',
        },
        {
          title: this.$t('sections.features.items.contracts.title'),
          icon: require('@/assets/icons/icone-contratos-min.png'),
          lottie: ContractsAnimation,
          shortDescription: this.$t(
            'sections.features.items.contracts.shortDescription'
          ),
          longDescription: this.$t(
            'sections.features.items.contracts.longDescription'
          ),
          borderClass: 'border-gradient-pink',
          textClass: 'bg-gdr-fonts-pink',
        },
        {
          title: this.$t('sections.features.items.emails.title'),
          icon: require('@/assets/icons/icone-lote-email.png'),
          lottie: EmailsAnimation,
          shortDescription: this.$t(
            'sections.features.items.emails.shortDescription'
          ),
          longDescription: this.$t(
            'sections.features.items.emails.longDescription'
          ),
          borderClass: 'border-gradient-purple',
          textClass: 'bg-gdr-fonts-purple',
        },
        {
          title: this.$t('sections.features.items.statements.title'),
          icon: require('@/assets/icons/icone-importacao-min.png'),
          lottie: StatementsAnimation,
          shortDescription: this.$t(
            'sections.features.items.statements.shortDescription'
          ),
          longDescription: this.$t(
            'sections.features.items.statements.longDescription'
          ),
          borderClass: 'border-gradient-dark-green',
          textClass: 'bg-gdr-fonts-dark-green',
        },
      ]
    },
    splideOptions() {
      return {
        type: 'fade',
        perPage: 1,
        arrows: true,
        pagination: true,
        gap: '1rem',
        start: this.selectedIndex,
        autoplay: true,
        rewind: true,
        interval: 4000,
        pauseOnHover: true,
      }
    },
  },
  watch: {
    selectedIndex(newIndex) {
      if (newIndex !== null && this.$refs.splide) {
        this.$nextTick(() => {
          this.$refs.splide.go(newIndex)
        })
      }
    },
  },
  mounted() {
    if (this.$refs.splide) {
      this.$refs.splide.splide.on('move', newIndex => {
        this.selectedIndex = newIndex
      })
    }
  },
  beforeUnmount() {
    if (this.$refs.splide && this.$refs.splide.splide) {
      this.$refs.splide.splide.off('move')
    }
  },
  methods: {
    toggleFeatureDetails(index) {
      this.selectedIndex = index
    },
  },
}
</script>

<style scoped>
.lottie-container {
  width: 100%;
  max-width: 400px;
  margin: 0 auto 24px auto;
}

@media (min-width: 768px) {
  .slide-content {
    flex-direction: row;
    text-align: left;
    gap: 30px;
    align-items: center;
  }

  .lottie-container {
    flex: 1;
    margin: 0;
  }

  .slide-text {
    flex: 1;
  }
}
.bg-functionalities {
  padding: 32px 24px;
  background-color: var(--c4);
}
.functionalities-header {
  text-align: center;
  margin-bottom: 48px;
}
.functionalities-header .title {
  color: var(--c1);
  margin-bottom: 16px;
}
.functionalities-header p {
  color: var(--c3);
  max-width: 800px;
  margin: 0 auto;
}

.functionalities-list {
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}
.item {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 15px;
  border-radius: 8px;
  padding: 25px;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  box-shadow: rgba(50, 50, 93, 0.1) 0px 13px 27px -5px,
    rgba(0, 0, 0, 0.2) 0px 8px 16px -8px;
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;
  background-color: var(--white);
  border: 2px solid transparent; /* Adicione uma borda para destaque */
}
.item:hover {
  transform: translateY(-5px);
  box-shadow: rgba(50, 50, 93, 0.15) 0px 20px 30px -10px,
    rgba(0, 0, 0, 0.25) 0px 15px 20px -10px;
}
.item.is-selected {
  transform: translateY(-5px);
  box-shadow: rgba(50, 50, 93, 0.2) 0px 25px 50px -12px,
    rgba(0, 0, 0, 0.3) 0px 18px 36px -18px;
  border-color: #007bff;
}
.item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 8px 8px 0 0;
  width: 100%;
  height: 7px;
}
.icon-function {
  width: 70px;
  height: 70px;
  object-fit: contain;
}
.item h2 {
  margin-bottom: 12px;
  font-size: 1.2rem;
}
.item p {
  color: var(--c3);
  font-size: 0.95rem;
  line-height: 1.5;
}

.details-section {
  margin-top: 48px;
}
.slide-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: var(--white);
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}
.slide-text h2 {
  color: var(--c1);
  margin-bottom: 16px;
}
.slide-text p {
  color: var(--c3);
  line-height: 1.6;
}

@media (min-width: 768px) {
  .slide-content {
    flex-direction: row;
    text-align: left;
    gap: 30px;
    align-items: center;
  }

  .lottie-container {
    flex: 1;
    margin: 0;
  }

  .slide-text {
    flex: 1;
  }
}

@media (max-width: 700px) {
  .slide-content {
    height: 600px;
  }
}

/* Gradientes (mantidos do seu original) */
.border-gradient-green::before {
  background: linear-gradient(270deg, #7be664 0%, #39a924 100%);
}
.border-gradient-blue::before {
  background: linear-gradient(270deg, #64a5e6 0%, #2469a9 100%);
}
.border-gradient-yellow::before {
  background: linear-gradient(270deg, #e6d564 0%, #a99824 100%);
}
.border-gradient-orange::before {
  background: linear-gradient(270deg, #e6a964 0%, #a97024 100%);
}
.border-gradient-red::before {
  background: linear-gradient(270deg, #e66464 0%, #a92424 100%);
}
.border-gradient-pink::before {
  background: linear-gradient(270deg, #e664b8 0%, #a9247e 100%);
}
.border-gradient-purple::before {
  background: linear-gradient(270deg, #a964e6 0%, #7024a9 100%);
}
.border-gradient-dark-green::before {
  background: linear-gradient(270deg, #64e682 0%, #24a945 100%);
}
.font-grd {
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.bg-gdr-fonts-green {
  background-image: linear-gradient(270deg, #7be664 0%, #39a924 100%);
}
.bg-gdr-fonts-blue {
  background-image: linear-gradient(270deg, #64a5e6 0%, #2469a9 100%);
}
.bg-gdr-fonts-yellow {
  background-image: linear-gradient(270deg, #e6d564 0%, #a99824 100%);
}
.bg-gdr-fonts-orange {
  background-image: linear-gradient(270deg, #e6a964 0%, #a97024 100%);
}
.bg-gdr-fonts-red {
  background-image: linear-gradient(270deg, #e66464 0%, #a92424 100%);
}
.bg-gdr-fonts-pink {
  background-image: linear-gradient(270deg, #e664b8 0%, #a9247e 100%);
}
.bg-gdr-fonts-purple {
  background-image: linear-gradient(270deg, #a964e6 0%, #7024a9 100%);
}
.bg-gdr-fonts-dark-green {
  background-image: linear-gradient(270deg, #64e682 0%, #24a945 100%);
}
:deep(.splide__arrow) {
  background: linear-gradient(
    90deg,
    var(--color-primary) 0%,
    var(--color-secondary) 100%
  );
  opacity: 1;
}
:deep(.splide__arrow svg) {
  fill: var(--white);
}
:deep(.splide__pagination__page.is-active) {
  background: var(--color-primary);
}
</style>
